import type { DropDownResult } from '@myosh/odin-components';
import { Dialog, isDropDownNoSelection } from '@myosh/odin-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GenericErrorResponse } from '../../../../@types/common';
import type { ViewConfig } from '../../../../@types/views';
import useViewConfigs from '../../../../hooks/use-view-configs';
import { useDeleteViewConfigMutation, useUpdateViewConfigMutation } from '../../../../redux/services/view';
import { promiseToast } from '../../../../services/notification.service';
import Notifications from '../../../notifications/notifications.component';
import type { View } from '../../view-name-editor.component';
import ViewNameEditor from '../../view-name-editor.component';
import useCanManageGlobalViews from '../hooks/use-can-manage-global-views';

interface ManageViewsModalProps {
  title?: string;
  visible: boolean;
  moduleId: number;
  selectedForm: DropDownResult;
  onClose: () => void;
}

export default function ManageViewsModal({ title, visible, onClose, moduleId, selectedForm }: ManageViewsModalProps) {
  const isAllForms = selectedForm.value === 0;
  const manageViewsToasterId = 'manage-views';
  const dialogId = 'manage-views-dialog';

  const { canManageGlobalViews, isLoading: isCanManageGlobalViewsLoading } = useCanManageGlobalViews(
    moduleId,
    selectedForm.value as number,
    isAllForms
  );

  const [deleteViewConfig] = useDeleteViewConfigMutation();
  const [updateViewConfig] = useUpdateViewConfigMutation();
  const [views, setViews] = useState<View[]>([]);

  const { t } = useTranslation();

  const { data: viewConfigs } = useViewConfigs(
    selectedForm && !isDropDownNoSelection(selectedForm) ? (selectedForm.value as number) : undefined,
    visible,
    moduleId,
    isAllForms
  );

  const viewData = useMemo(() => {
    if (isCanManageGlobalViewsLoading) {
      return [];
    }

    return (
      viewConfigs
        ?.map((view: ViewConfig) => {
          // default views are not ediable,
          // global views are only editable if the user has the 'canManageGlobalViews' permission
          // user views are editable by users
          const canEdit = view.defaultConfig ? false : view.global ? canManageGlobalViews : true;

          return {
            id: view.id,
            name: view.name,
            isGlobal: view.global,
            canEdit: canEdit,
          } as View;
        })
        //Sorts global views first and then alphabetically by name
        .sort((view1, view2) =>
          view1.isGlobal !== view2.isGlobal ? (view1.isGlobal ? -1 : 1) : view1.name?.localeCompare(view2.name)
        )
    );
  }, [viewConfigs, isCanManageGlobalViewsLoading, canManageGlobalViews]);

  useEffect(() => setViews(viewData ?? []), [viewData]);

  const handleChangeItem = (id: number, name: string) => {
    setViews(views.map((view) => (view.id === id ? { ...view, name } : view)));
    const promise = updateViewConfig({ id, name }).unwrap();
    promise.catch(() => {
      setViews(views);
    });
    promiseToast(
      promise,
      {
        loading: t('performing-save'),
        success: t('view-successfull-update'),
        error: (error) => {
          const errorResponse = (error as GenericErrorResponse).data?.validation?.responseErrors?.[0];
          return errorResponse?.message ?? t('error-rename-view');
        },
      },
      {
        toasterId: manageViewsToasterId,
      }
    );
  };

  const handleRemoveItem = (id: number) => {
    setViews(views.filter((view) => view.id !== id));

    promiseToast(
      deleteViewConfig(id)
        .unwrap()
        .catch(() => {
          // revert delete if an error occurred
          setViews(views);
        }),
      {
        loading: t('performing-save'),
        success: t('view-successfull-delete'),
        error: (error) => {
          const errorResponse = (error as GenericErrorResponse).data?.validation?.responseErrors?.[0];
          return errorResponse?.message ?? t('error-rename-view');
        },
      },
      {
        toasterId: manageViewsToasterId,
      }
    );
  };

  return (
    <>
      <Dialog
        id={dialogId}
        header={title ?? t('manage-views')}
        visible={visible}
        onClose={onClose}
        containerClassName="sm:w-96"
      >
        {views.map((view, index) => (
          <ViewNameEditor
            key={view.id}
            view={view}
            isEven={index % 2 === 0}
            onChange={handleChangeItem}
            onRemove={handleRemoveItem}
          />
        ))}
        <Notifications appendTo={document.getElementById(dialogId) ?? document.body} toasterId={manageViewsToasterId} />
      </Dialog>
    </>
  );
}
